import { isPlainObject } from "lodash";

const SOURCE_ATTRIBUTE = "data-options-source";
const SIGNATURE_ATTRIBUTE = "data-options-signature";

const optionsSignature = (options) =>
  options.map(({ value, label }) => `${value}\u0000${label}`).join("\u0001");

const ensurePlaceholder = (select) => {
  if (Array.from(select.options).some((option) => option.value === "")) return;
  const placeholder = select.ownerDocument.createElement("option");
  placeholder.value = "";
  placeholder.hidden = true;
  placeholder.textContent = select.getAttribute("data-placeholder") || "";
  select.insertBefore(placeholder, select.firstChild);
};

const isEmptyValue = (value) =>
  value === undefined || value === null || String(value) === "";

/**
 * A relation bound to a remote select (e.g. `project.source`) is read as the
 * related row; the select only knows its id.
 */
export const sourceOptionValueOf = (value) =>
  isPlainObject(value) ? value.id : value;

const selectedOptionValue = (list, selectedValue) => {
  if (isEmptyValue(selectedValue)) {
    const fallback = list.find(({ isDefault }) => isDefault);
    return fallback ? String(fallback.value) : "";
  }
  return list.some(({ value }) => String(value) === String(selectedValue))
    ? String(selectedValue)
    : "";
};

/**
 * Replaces the options only when the list changed, so re-hydrating on every
 * keystroke never resets an open select. The disabled state is left alone:
 * it belongs to the slot conditions (`enabledWhen`) and the project lock.
 */
export const fillSourceOptions = (select, options, selectedValue) => {
  const list = Array.isArray(options) ? options : [];
  const signature = optionsSignature(list);
  if (select.getAttribute(SIGNATURE_ATTRIBUTE) !== signature) {
    for (const option of Array.from(select.options)) {
      if (option.value !== "") option.remove();
    }
    ensurePlaceholder(select);
    for (const { value, label } of list) {
      const option = select.ownerDocument.createElement("option");
      option.value = value;
      option.textContent = label;
      select.appendChild(option);
    }
    select.setAttribute(SIGNATURE_ATTRIBUTE, signature);
  }
  const selected = selectedOptionValue(list, selectedValue);
  if (select.value !== selected) select.value = selected;
  select.setAttribute("data-empty", select.value ? "false" : "true");
};

export const setupSourceSelects = (doc, { loadOptions } = {}) => {
  const selects = doc
    ? Array.from(doc.querySelectorAll(`select[${SOURCE_ATTRIBUTE}]`))
    : [];
  if (!selects.length || typeof loadOptions !== "function") {
    return { refresh: () => undefined };
  }

  const loaded = {};
  const optionsOf = (source) => {
    if (!loaded[source]) {
      loaded[source] = Promise.resolve()
        .then(() => loadOptions(source))
        .catch(() => []);
    }
    return loaded[source];
  };

  const refresh = () => {
    for (const select of selects) {
      const source = select.getAttribute(SOURCE_ATTRIBUTE);
      optionsOf(source).then((options) => {
        if (!select.isConnected) return;
        const restore = select.hasAttribute("data-restore-value")
          ? select.getAttribute("data-restore-value")
          : select.value;
        fillSourceOptions(select, options, restore);
      });
    }
  };

  refresh();
  return { refresh };
};
